import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { take } from 'rxjs/operators';

import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { AssessmentPage } from 'src/app/ai-assessments/models/assessments.interface';
import { AssessmentInviteeStatusEnum } from 'src/app/api/models/assessments/assessment.enum';
import { AssessmentsService } from 'src/app/ai-assessments/services/assessments.service';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { CollaborationLoggerService } from './collaboration-logger.service';
import { CollaborationTokenStoreService } from './collaboration-token-store.service';

@Injectable()
export class CollaborationNavigationService {
	private readonly logOptions = { component: 'CollaborationNavigationService' };

	constructor(
		private router: Router,
		private assessmentsService: AssessmentsService,
		private collaborationTokenStoreService: CollaborationTokenStoreService,
		private contentPipe: ContentPipe,
		private logger: CollaborationLoggerService,
	) {}

	getOpenAssessments(assessments: AssessmentPage[], collaboratorEmail: string): AssessmentPage[] {
		return assessments
			.filter(
				(assessment) =>
					assessment.collabInvites?.some(
						(invite) =>
							invite.email === collaboratorEmail &&
							invite.status === AssessmentInviteeStatusEnum.Invited &&
							invite.isDone === false,
					),
			)
			.sort((a, b) => {
				const aDate = new Date(a.collabInvites.find((invite) => invite.email === collaboratorEmail)?.invitedAt);
				const bDate = new Date(b.collabInvites.find((invite) => invite.email === collaboratorEmail)?.invitedAt);
				return bDate.getTime() - aDate.getTime();
			});
	}

	navigateToNextOpenAssessment(): void {
		const collaboratorEmail = this.collaborationTokenStoreService.getCollaboratorEmail();
		this.logger.info('Looking for next open assessment for collaborator', {}, this.logOptions);

		this.assessmentsService
			.selectAssessments(true)
			.pipe(take(1))
			.subscribe((assessments) => {
				const openAssessments = this.getOpenAssessments(assessments as AssessmentPage[], collaboratorEmail);
				const nextAssessment = openAssessments?.[0];

				if (nextAssessment) {
					this.logger.info(
						'Navigating to next open assessment',
						{
							nextAssessmentId: nextAssessment.id,
							totalPendingInvites: openAssessments.length,
						},
						this.logOptions,
					);

					this.router.navigate([
						RoutesManager.collaboration,
						RoutesManager.collaborationAssessmentPage,
						nextAssessment.id,
					]);
				} else {
					this.logger.warn(
						'No open assessment found for collaborator',
						{
							totalAssessments: (assessments as AssessmentPage[]).length,
						},
						this.logOptions,
					);
				}
			});
	}

	navigateToAssessmentPage(assessmentId: string): void {
		this.logger.info('Navigating to assessment page', { assessmentId }, this.logOptions);

		this.router.navigate([RoutesManager.collaboration, RoutesManager.collaborationAssessmentPage, assessmentId]);
	}

	navigateToDeletedAssessmentPage(): void {
		const errorMessage = this.contentPipe.transform('ai-assessments-collaboration.assessmentWasDeleted');
		this.collaborationTokenStoreService.setCollaborationErrorState(errorMessage, 404);

		this.router.navigate(
			[
				RoutesManager.collaboration,
				RoutesManager.collaborationAssessmentExpired,
				this.collaborationTokenStoreService.getActiveAssessmentId() ?? '',
			],
			{
				state: {
					status: 404,
					error: errorMessage,
					skipGuard: true,
				},
			},
		);
	}

	navigateToAuthenticationErrorPage(error: Error | string): void {
		const callerStack = new Error('navigateToAuthenticationErrorPage called').stack;

		let errorMessage = '';
		let status: number | undefined = undefined;
		let originalStack: string | undefined = undefined;

		if (error instanceof HttpErrorResponse) {
			errorMessage =
				typeof error.error === 'string'
					? error.error
					: error.error?.message ?? error.message ?? 'Unknown authentication error';
			status = error.status;
			originalStack = error.error?.stack;
		} else if (error instanceof Error) {
			errorMessage = error.message;
			originalStack = error.stack;
		} else if (typeof error === 'object' && error !== null) {
			errorMessage = (error as { message?: string; status?: number }).message ?? JSON.stringify(error);
			status = (error as { status?: number }).status ?? undefined;
		} else {
			errorMessage = String(error);
		}

		this.logger.warn(
			'Navigating to authentication error page',
			{
				error: errorMessage,
				status,
				errorType: (error as object)?.constructor?.name ?? typeof error,
				errorStack: originalStack,
				callerStack,
				...(error instanceof HttpErrorResponse && {
					httpDetails: {
						url: error.url,
						method: (error as any).method,
						statusText: error.statusText,
						headers: error.headers?.keys?.()?.reduce((acc: Record<string, string>, key: string) => {
							acc[key] = error.headers.get(key) ?? '';
							return acc;
						}, {}),
					},
				}),
			},
			this.logOptions,
		);

		if (status === 401) {
			errorMessage = this.contentPipe.transform('ai-assessments-collaboration.generalAuthFailureMessage', {
				params: {
					error: errorMessage,
				},
			});
		} else if (status === 403) {
			errorMessage = this.contentPipe.transform('ai-assessments-collaboration.youDontHaveAccess');
		}

		this.collaborationTokenStoreService.setCollaborationErrorState(errorMessage, status);

		const route = [
			RoutesManager.collaboration,
			status === 403 ? RoutesManager.collaborationAssessmentExpired : RoutesManager.collaborationAuthenticationError,
		];
		if (status === 403) {
			route.push(this.collaborationTokenStoreService.getActiveAssessmentId() ?? '');
		}
		this.router.navigate(route, {
			state: {
				status,
				error: errorMessage,
				skipGuard: true,
			},
		});
	}
}
